import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Event, NavigationEnd, Router } from '@angular/router';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TranslocoService } from '@jsverse/transloco';
import { filter, firstValueFrom } from 'rxjs';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { FieldInterventionEntityBloc } from '@connect-services/client/blocs/field-intervention-entity.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import {
    FieldInterventionEntityItemsListComponent,
    ItemWithState,
} from '@connect-services/client/components/field-intervention-entity-details/field-intervention-entity-items-list/field-intervention-entity-items-list.component';
import { TitleComponent } from '@connect-services/client/components/field-intervention-title/title/title.component';
import {
    FieldInterventionEntityAttributions,
    FieldInterventionPlanningModalComponent,
} from '@connect-services/client/components/modals/field-intervention-planning-modal/field-intervention-planning-modal.component';
import { fieldInterventionFormsRegexp } from '@connect-services/client/guards/can-navigate.guard';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { ActionsEnum } from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { EntitiesTableByLayerService } from '@connect-services/client/services/entities-table-by-layer.service';
import { FieldInterventionService } from '@connect-services/client/services/field-intervention.service';
import { FieldInterventionPermissionService } from '@connect-services/client/services/field-intervention-permission.service';
import { excludedAttributes } from '@connect-services/client/services/layer.service';
import { TranslateService } from '@connect-services/client/services/translate.service';
import { UpdateFieldInterventionService } from '@connect-services/client/services/update-field-intervention.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import {
    FieldIntervention,
    FieldInterventionEntitiesService,
    FieldInterventionEntity,
    FieldInterventionForm,
    Person,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

export interface ButtonConfiguration {
    action: () => void;
    icon: IconProp;
    label: string;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        FieldInterventionEntityItemsListComponent,
        LoadingComponent,
        RessourcesModule,
        TitleComponent,
    ],
    selector: 'app-field-intervention-entity-details',
    styleUrl: './field-intervention-entity-details.component.scss',
    templateUrl: './field-intervention-entity-details.component.html',
})
export class FieldInterventionEntityDetailsComponent {
    protected readonly ActionsEnum = ActionsEnum;
    protected readonly answersActionButtonConfig = computed<ButtonConfiguration>((): ButtonConfiguration =>
        this.fieldInterventionService.isEntityResultDisplayed()
            ? {
                  action: () => this.navigateToEntitiesPage(),
                  icon: ['fas', 'eye-slash'],
                  label: 'hideAnswers',
              }
            : {
                  action: () => this.navigateToAnswersFormPage(),
                  icon: ['fas', 'eye'],
                  label: 'showAnswers',
              },
    );
    protected readonly ButtonType = ButtonType;
    protected readonly currentPanelEntity: WritableSignal<string | null> = signal(null);
    protected readonly dataAttributes: WritableSignal<Record<'businessIdentifier' | string, unknown> | null> =
        signal(null);
    protected readonly favoriteDataAttributes: WritableSignal<Record<'businessIdentifier' | string, unknown> | null> =
        signal(null);
    protected readonly fieldInterventionEntity: WritableSignal<FieldInterventionEntity | null> = signal(null);
    protected readonly fieldInterventionFormsRegexp = fieldInterventionFormsRegexp;
    protected readonly fieldInterventionPermissionService = inject(FieldInterventionPermissionService);
    protected fieldInterventionService = inject(FieldInterventionService);
    protected readonly formItemsWithState: Signal<ItemWithState[]> = computed((): ItemWithState[] => [
        ...this.fieldInterventionEntity()!.forms.map((form: FieldInterventionForm): ItemWithState => ({
            item: form,
            updated: false,
        })),
        ...this.updatedForms().map((form: FieldInterventionForm): ItemWithState => ({ item: form, updated: true })),
    ]);
    protected readonly forms: Signal<FieldInterventionForm[]> = computed(() =>
        this.showAll().forms
            ? this.fieldInterventionEntity()!.forms
            : this.fieldInterventionEntity()!.forms?.slice(0, 3),
    );
    protected readonly isFieldInterventionStatusToPlan: Signal<boolean> = computed(
        () => this.fieldInterventionBloc.value()!.status === FieldIntervention.StatusEnum.TO_PLAN,
    );
    protected readonly isLoading = signal(true);
    protected readonly Object = Object;
    protected readonly people: Signal<Person[]> = computed(() =>
        this.showAll().people
            ? (this.fieldInterventionEntity()!.people ?? [])
            : (this.fieldInterventionEntity()!.people?.slice(0, 3) ?? []),
    );
    protected readonly peopleItemsWithState: Signal<ItemWithState[]> = computed((): ItemWithState[] => [
        ...(this.fieldInterventionEntity()!.people ?? []).map((person: Person): ItemWithState => ({
            item: person,
            updated: false,
        })),
        ...this.updatedPeople().map((person: Person): ItemWithState => ({ item: person, updated: true })),
    ]);
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly router = inject(Router);
    protected readonly showAll = signal({ dataAttributes: false, forms: false, people: false });
    protected readonly translateService = inject(TranslateService);
    protected readonly updatedForms: Signal<FieldInterventionForm[]> = computed((): FieldInterventionForm[] => {
        const updateEntity: FieldInterventionEntity | undefined = this.updateFieldInterventionService
            .updatedEntities()
            .find((entity: FieldInterventionEntity): boolean => entity.id === this.fieldInterventionEntity()?.id);

        if (!updateEntity || !this.fieldInterventionEntity()) {
            return [];
        }

        return (updateEntity.forms ?? []).filter(
            (form: FieldInterventionForm): boolean =>
                !(this.fieldInterventionEntity()?.formIds ?? []).includes(form.id),
        );
    });
    protected readonly updatedPeople: Signal<Person[]> = computed(() => {
        const updateEntity: FieldInterventionEntity | undefined = this.updateFieldInterventionService
            .updatedEntities()
            .find((entity) => entity.id === this.fieldInterventionEntity()?.id);

        if (!updateEntity || !this.fieldInterventionEntity()) {
            return [];
        }

        return (updateEntity.people ?? []).filter(
            (person) => !(this.fieldInterventionEntity()?.peopleIds ?? []).includes(person.id),
        );
    });

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly destroyRef = inject(DestroyRef);
    private readonly entitiesTableByLayerService = inject(EntitiesTableByLayerService);
    private readonly fieldInterventionBloc = inject(FieldInterventionBloc);
    private readonly fieldInterventionEntitiesService = inject(FieldInterventionEntitiesService);
    private readonly fieldInterventionEntityBloc = inject(FieldInterventionEntityBloc);
    private readonly matDialog = inject(MatDialog);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly translocoService = inject(TranslocoService);
    private readonly updateFieldInterventionService = inject(UpdateFieldInterventionService);

    public constructor() {
        this.destroyRef.onDestroy(() => {
            this.fieldInterventionService.isEntityResultDisplayed.set(false);
        });

        this.currentPanelEntity.set(this.fieldInterventionService.getDetailPanelUuidFromUrl());

        effect(() => {
            if (!this.fieldInterventionEntityBloc.value() || !this.currentPanelEntity()) {
                return;
            }

            this.fieldInterventionEntity.set(this.fieldInterventionEntityBloc.value());

            this.setDataAttributes();
        });

        this.router.events
            .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((): void => {
                this.currentPanelEntity.set(this.fieldInterventionService.getDetailPanelUuidFromUrl());
            });
    }

    public handleRemoveButton(): void {
        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('delete'),
                description: this.translocoService.translate('allTheDataWillBeDeleted'),
                title: this.translocoService.translate('doYouWantToDelete', { entity: 'fieldInterventionEntity' }),
                type: ModalType.DANGER,
            },
        });

        dialogRef.afterClosed().subscribe(async (result: ModalResultEnum) => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }

            await firstValueFrom(
                this.fieldInterventionEntitiesService.fieldInterventionEntityControllerDelete({
                    id: this.fieldInterventionEntity()!.id,
                }),
            );

            await this.fieldInterventionService.toggleFieldInterventionEntityDetailPanel(
                this.fieldInterventionEntity()!.id,
            );

            await this.entitiesTableByLayerService.fetchEntities();
        });
    }

    public async navigateToAnswersFormPage(): Promise<void> {
        await this.navigateToPage(RoutePathEnum.FIELD_INTERVENTION_FORMS, true);
    }

    public async navigateToEntitiesPage(): Promise<void> {
        await this.navigateToPage(RoutePathEnum.FIELD_INTERVENTION_ENTITIES, false);
    }

    public openPlanningModal(): void {
        const dialogRef = this.matDialog.open(FieldInterventionPlanningModalComponent, {
            data: {
                hasLoader: false,
                numberOfEntities: 1,
                preselectedFormIds: this.fieldInterventionEntity()!.formIds,
                preselectedPeopleIds: this.fieldInterventionEntity()!.peopleIds,
                startPlanning: async (attrib: FieldInterventionEntityAttributions): Promise<void> => {
                    await firstValueFrom(
                        this.fieldInterventionEntitiesService.fieldInterventionEntityControllerPatch({
                            id: this.fieldInterventionEntity()!.id,
                            updateFieldInterventionEntityDto: {
                                forms: attrib.forms.map((form: FieldInterventionForm) => form.id),
                                people: attrib.people.map((person: Person) => person.id),
                                status: FieldInterventionEntity.StatusEnum.PLANNED,
                            },
                        }),
                    );
                },
                updateMode: false,
            },
            width: '45rem',
        });

        dialogRef.afterClosed().subscribe(async (result?: FieldInterventionEntityAttributions): Promise<void> => {
            if (!result) {
                return;
            }

            await this.fieldInterventionEntityBloc.load(
                {
                    id: this.fieldInterventionEntity()!.id,
                    onlyBookmarked: true,
                },
                {
                    refresh: true,
                },
            );
        });
    }

    public toggleList(property: keyof ReturnType<typeof this.showAll>): void {
        this.showAll.update((state) => ({ ...state, [property]: !state[property] }));
    }

    private filterAttributes<T extends Record<string, unknown>>(
        source: T | null,
        excludeKeys: string[] = [],
    ): Partial<T> {
        if (!source) {
            return {};
        }

        return Object.fromEntries(Object.entries(source).filter(([key]) => !excludeKeys.includes(key))) as Partial<T>;
    }

    private async navigateToPage(routePath: RoutePathEnum, isEntityResultDisplayed: boolean): Promise<void> {
        const path = [
            RoutePathEnum.PROJECTS,
            this.projectBloc.value()?.id,
            RoutePathEnum.FIELD_INTERVENTIONS,
            this.fieldInterventionBloc.value()?.id,
            routePath,
        ];

        await this.router.navigate(
            [
                ...path,
                {
                    outlets: { detailPanel: this.fieldInterventionEntity()!.id },
                },
            ],
            {
                queryParams: { ...this.activatedRoute.snapshot.queryParams },
                queryParamsHandling: 'merge',
            },
        );

        this.fieldInterventionService.isEntityResultDisplayed.set(isEntityResultDisplayed);
    }

    private setDataAttributes(): void {
        this.favoriteDataAttributes.set(
            this.filterAttributes(this.fieldInterventionEntity()!.dataAttributes, excludedAttributes),
        );

        const fieldInterventionEntity = this.entitiesTableByLayerService
            .entities()
            .find((entity) => entity.id === this.fieldInterventionEntity()?.id);

        if (!fieldInterventionEntity) {
            return;
        }

        const favoriteKeys = this.favoriteDataAttributes() ? Object.keys(this.favoriteDataAttributes()!) : [];
        const excludeKeys = [...excludedAttributes, ...favoriteKeys];
        this.dataAttributes.set(this.filterAttributes(fieldInterventionEntity!.dataAttributes, excludeKeys));
    }
}
